import Barcode from 'react-barcode';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Printer, Barcode as BarcodeIcon, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';

interface BarcodeInfo {
  id: number;
  barcode: string;
  type: string;
  isPrimary: boolean;
  isExternal: boolean;
}

interface BarcodeDisplayProps {
  barcodes: BarcodeInfo[];
  skuCode?: string;
  itemName?: string;
  showSkuBarcode?: boolean;
}

// Map backend barcode types to react-barcode format strings
const BARCODE_FORMAT_MAP: Record<string, string> = {
  'CODE128': 'CODE128',
  'EAN13': 'EAN13',
  'EAN8': 'EAN8',
  'UPC': 'UPC',
  'CODE39': 'CODE39',
  'INTERNAL': 'CODE128',
  'EXTERNAL': 'CODE128',
};

/**
 * Validates if a value can be rendered with the given barcode format.
 * Falls back to CODE128 (which accepts any ASCII string) if invalid.
 */
function getSafeFormat(value: string, requestedFormat: string): string {
  if (requestedFormat === 'EAN13') {
    // EAN-13 needs exactly 12 or 13 digits with a valid check digit
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 12 && digits.length !== 13) return 'CODE128';
    if (digits.length === 13) {
      const checkDigit = calculateEan13Check(digits.slice(0, 12));
      if (checkDigit !== Number(digits[12])) return 'CODE128';
    }
  }
  if (requestedFormat === 'EAN8') {
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 7 && digits.length !== 8) return 'CODE128';
  }
  if (requestedFormat === 'UPC') {
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 12) return 'CODE128';
  }
  return requestedFormat;
}

function calculateEan13Check(first12: string): number {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number(first12[i]) * (i % 2 === 0 ? 1 : 3);
  }
  return (10 - (sum % 10)) % 10;
}

export function BarcodeDisplay({ barcodes, skuCode, itemName }: BarcodeDisplayProps) {
  const [flashId, setFlashId] = useState<string | null>(null);

  const copyToClipboard = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setFlashId(id);
      setTimeout(() => setFlashId(null), 400);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePrint = (barcode: string, format: string, sku?: string) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const doc = printWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
<head>
  <title>Código de Barras - ${barcode}</title>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
  <style>
    body {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      margin: 0;
      font-family: sans-serif;
    }
    .label {
      text-align: center;
      margin-bottom: 10px;
      font-size: 12px;
      color: #666;
    }
    .sku {
      text-align: center;
      margin-top: 8px;
      font-size: 11px;
      color: #888;
      font-family: monospace;
    }
    @media print {
      body { padding: 20mm; }
    }
  </style>
</head>
<body>
  ${itemName ? `<div class="label">${itemName}</div>` : ''}
  <svg id="barcode"></svg>
  ${sku ? `<div class="sku">SKU: ${sku}</div>` : ''}
  <script>
    JsBarcode("#barcode", "${barcode}", {
      format: "${format}",
      width: 2,
      height: 80,
      displayValue: true,
      fontSize: 14,
      margin: 10
    });
    window.onload = function() {
      setTimeout(function() {
        window.print();
        window.close();
      }, 250);
    };
  <\/script>
</body>
</html>`);
    doc.close();
  };

  // Only use inventory barcodes (no SKU barcode generation)
  if (barcodes.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-4">
        <BarcodeIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>Nenhum código de barras cadastrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {barcodes.map((bc) => {
        const requestedFormat = BARCODE_FORMAT_MAP[bc.type] || 'CODE128';
        const format = getSafeFormat(bc.barcode, requestedFormat);
        const bcId = String(bc.id);
        const label = bc.isExternal ? 'Código Externo (Fornecedor)' : 'Código de Inventário';
        const Icon = bc.isExternal ? ExternalLink : BarcodeIcon;

        return (
          <Card key={bcId} className="overflow-hidden">
            <CardHeader className="py-2 px-3 md:px-4 bg-muted/50">
              <CardTitle className="text-xs md:text-sm font-medium flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                  <Icon className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </div>
                <span className="text-[10px] md:text-xs text-muted-foreground font-mono shrink-0">
                  {bc.type}{format !== requestedFormat ? ` → ${format}` : ''}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 md:p-4">
              <div className="flex flex-col items-center">
                <div className="bg-white p-2 md:p-4 rounded-lg overflow-hidden max-w-full w-full flex flex-col items-center [&_svg]:max-w-full [&_svg]:h-auto">
                  <Barcode
                    value={bc.barcode}
                    format={format as any}
                    width={1.5}
                    height={50}
                    fontSize={0}
                    margin={3}
                    displayValue={false}
                  />
                  {/* Clickable barcode code */}
                  <button
                    type="button"
                    onClick={() => copyToClipboard(bc.barcode, `bc-${bcId}`)}
                    className={cn(
                      "block w-full text-center mt-1 py-1 px-1 md:px-2 rounded-md font-mono text-xs md:text-sm text-gray-800 cursor-pointer transition-all duration-150",
                      "hover:bg-blue-50 hover:shadow-[0_0_8px_rgba(59,130,246,0.25)]",
                      "active:scale-[0.97]",
                      flashId === `bc-${bcId}`
                        ? "bg-blue-100 shadow-[0_0_12px_rgba(59,130,246,0.4)]"
                        : ""
                    )}
                    title="Clique para copiar"
                  >
                    {bc.barcode}
                  </button>
                  {/* Clickable SKU code */}
                  {skuCode && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(skuCode, `sku-${bcId}`)}
                      className={cn(
                        "block w-full text-center mt-1 py-0.5 px-1 md:px-2 rounded-md font-mono text-[10px] md:text-xs text-gray-500 cursor-pointer transition-all duration-150",
                        "hover:bg-blue-50 hover:shadow-[0_0_8px_rgba(59,130,246,0.2)]",
                        "active:scale-[0.97]",
                        flashId === `sku-${bcId}`
                          ? "bg-blue-100 shadow-[0_0_12px_rgba(59,130,246,0.4)]"
                          : ""
                      )}
                      title="Clique para copiar SKU"
                    >
                      SKU: {skuCode}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrint(bc.barcode, format, skuCode)}
                    className="gap-1"
                  >
                    <Printer className="h-3 w-3" />
                    Imprimir
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
